<template>
    <div class="services-grid services" ref="servicesElement">
        <ServiceGridItem
            v-for="(item, index) in gridItems"
            :key="`grid-${index}`"
            :service="item.service"
            :index="index"
            :itemHeight="itemHeight"
            :setGridRef="setGridRef"
        />
    </div>
</template>

<script setup lang="ts">
    import type { IService } from "@/data/model/Service";
    import { type ComponentPublicInstance, computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";

    import ServiceGridItem from "@/app/components/service/ServiceGridItem.vue";

    const props = defineProps<{
        services: IService[];
    }>();

    interface GridItem {
        service: IService | null;
        index: number;
    }

    const servicesElement = ref<HTMLElement>();
    const gridRefs = ref<(HTMLElement | null)[]>([]);
    const itemHeight = ref(0);

    const gridItems = computed<GridItem[]>(() => {
        if (props.services.length === 0) return [];

        const sortOrderNumbers = props.services.map((c) => c.order);
        const maxSortOrderNumber = Math.max(...sortOrderNumbers);

        const items: GridItem[] = [];
        for (let i = 0; i <= maxSortOrderNumber; i++) {
            const service = props.services.find((c) => c.order == i) ?? null;
            items.push({
                service: service,
                index: i,
            });
        }
        return items;
    });

    function ensureArraySize(arr: (HTMLElement | null)[], index: number) {
        while (arr.length <= index) {
            arr.push(null);
        }
    }

    function setGridRef(el: Element | ComponentPublicInstance | null, index: number) {
        if (el && el instanceof HTMLElement) {
            ensureArraySize(gridRefs.value, index);
            gridRefs.value[index] = el;
        }
    }

    function getServicesContainer() {
        return servicesElement.value ? $(servicesElement.value) : $();
    }

    function getItemHeight() {
        const ratio = 144 / 188;
        const servicesContentWidth = getServicesContainer().width();
        if (!servicesContentWidth || servicesContentWidth <= 0) {
            return 0;
        }
        const gridItemWidth = servicesContentWidth / 2;
        return gridItemWidth * ratio;
    }

    function resize($el?: JQuery) {
        const newHeight = getItemHeight();
        itemHeight.value = newHeight;

        if ($el !== undefined) {
            $el.height(newHeight);
            return;
        }

        const $services = getServicesContainer();
        if ($services.length === 0) return;

        $services.find("> .grid").each((i, e) => {
            $(e).height(newHeight);
        });
    }

    function animateServices() {
        gridRefs.value.forEach((gridEl, index) => {
            if (!gridEl) return;
            const $grid = $(gridEl);
            if ($grid.hasClass("invis")) {
                resize($grid);
                return;
            }
            $grid.delay(index * 90).queue(function (this: any, e) {
                $(this).addClass("shown");
                resize($(this));
                e();
            });
        });
    }

    function hideServices() {
        gridRefs.value.forEach((gridEl) => {
            if (gridEl) {
                $(gridEl).removeClass("shown");
            }
        });
    }

    async function refresh() {
        await nextTick();
        resize();
        animateServices();
    }

    onMounted(() => {
        resize();
        animateServices();
        $(window).bind("resize.services", () => resize());
    });

    onBeforeUnmount(() => {
        $(window).unbind("resize.services");
    });

    watch(
        () => props.services,
        () => {
            refresh();
        },
        { deep: true }
    );

    defineExpose({
        animateServices,
        hideServices,
        refresh,
        resize,
    });
</script>

<style lang="scss">
    @use "./../../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../../assets/styles/scss/shared/_mixins.scss";
    @use "./../../../../assets/styles/scss/shared/_variables.scss";

    .services-grid {
        background-color: #fff;
        padding: 15px 5px;

        // kiosk usually shows only two services, so center the rows vertically
        .kiosk-mode & {
            display: flex;
            flex-wrap: wrap;
            align-content: center;
            min-height: 100%;
            box-sizing: border-box;
        }

        > .grid {
            width: 50%;
            display: inline-block;
            vertical-align: top;
            padding: 7px;
            box-sizing: border-box;
            opacity: 0;
            transform: translate(0, -30px);
            transition:
                opacity 0.5s variables.$easeOutExpo,
                transform 0.5s variables.$easeOutExpo;

            &.shown {
                opacity: 1;
                transform: translate(0, 0);
            }

            &.invis {
                opacity: 0 !important;
                transform: translate(0, 0);
            }
        }
    }
</style>
